/*
 * The unified shell's top bar (PLT-6527): the design system's 50px white bar as the
 * corporate shell ships it (`dma-v2-view/dashboard/top-header`) and the prototypes drew it
 * (`internal/proto/proto-shell.module.css`), with the investor side's controls in its slots.
 * The height comes in through the frame's `--unified-shell-header-height`; everything on
 * the scale uses the token.
 */
.bar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: var(--space__lg);
  block-size: var(--unified-shell-header-height, 50px);
  padding-inline: var(--space__xl);
  border-block-end: 1px solid var(--color__gray-300);
  background: var(--color__white);
}

/* Brand slot: the menu button below `md`, then the wordmark, linking home. */
.brand {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: var(--space__xs);
}

/*
 * The menu button that opens the navigation drawer (PLT-6738). Absent above `md`, where the rail
 * is a pane beside the page and there is nothing to open — a control that opens something already
 * on screen is worse than no control. The 40px box is the same hit area every other bar control
 * degrades to, which is what keeps the row one height at every width.
 */
.menuButton {
  display: none;
  flex: none;
  align-items: center;
  justify-content: center;
  inline-size: var(--space__3xl);
  block-size: var(--space__3xl);
  padding: 0;
  border: none;
  border-radius: var(--border-radius__lg);
  background: transparent;
  color: var(--color__gray-900);
  cursor: pointer;
}

.menuButton:hover {
  background-color: var(--bgColor-neutral-muted);
}

.menuButton:focus-visible {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
}

.logoLink {
  display: flex;
  align-items: center;
  block-size: var(--space__3xl);
  border-radius: var(--border-radius__sm);
  color: var(--color__gray-900);
}

.logoLink:focus-visible {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
}

.logoFull {
  display: block;
}

.logoSymbol {
  display: none;
}

/* A console's name after the wordmark: the API reference bar's treatment (`api-docs-top-bar.module.css`). */
.productName {
  display: flex;
  align-items: center;
  gap: var(--space__md);
  margin-inline-start: var(--space__sm);
  color: var(--color__gray-900);
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__semibold);
  line-height: var(--line-height__sm);
  white-space: nowrap;
}

/* A hairline between the wordmark and the name, the height of the wordmark's cap. */
.productNameDivider {
  inline-size: 1px;
  block-size: var(--space__lg);
  background: var(--color__gray-300);
}

/*
 * The context slot: the workspace pill, for a user who also has the DMA dashboard. `xs` here on top
 * of the brand slot's own `xs` gap puts it `sm` from the wordmark, where the DMA dashboard's bar puts
 * the same pill, so it does not move when the user switches between the two bars.
 */
.context {
  display: flex;
  align-items: center;
  margin-inline-start: var(--space__xs);
}

/* Actions and account slots, right-aligned: Ask AI, search, Feedback, Help, then the account square. */
.actions {
  display: flex;
  flex: 1 1 auto;
  min-inline-size: 0;
  align-items: center;
  justify-content: flex-end;
}

/*
 * A layout-transparent box around the search, so the field keeps sizing itself against the
 * actions row exactly as it did when it was a direct child. It exists only to be switched off
 * below `md`, where the drawer carries the search instead — `display: contents` is what lets one
 * wrapper do that without becoming a flex item that changes how the field shrinks.
 */
.searchSlot {
  display: contents;
}

/*
 * The search field in the actions slot. It shrinks before the controls beside it do, and no
 * lower than a field a fund name still fits in.
 *
 * The `sm` on both sides is what keeps the pills either side of it off its edge: they carry
 * their hover ground to their own edge, so the space has to come from the field between them.
 *
 * It is the design system's `Input`, wearing the bar's own geometry through that component's
 * custom-property hooks rather than through a second set of rules: 40px tall, `full` radius, a
 * `gray-100` well with no border, and `lg` of air before the magnifier. At the component's own
 * defaults — 32px, `md` radius, a hairline on white, 8px to the glyph — it was the one control
 * in the cluster that did not match its neighbours, and a bordered white box inside a white bar
 * reads as an outline rather than as a place to type (PLT-6744).
 *
 * Why the search and not every input: a form field sits on the page, where a hairline on white
 * is the instrument look the whole system is built on (`DESIGN.md`). This one sits in chrome,
 * between two `full`-radius pills, where an inset well is the licensed use of a gray tint
 * (`design/10-foundations.md` "The page is always white") and the tinted tier is as loud as the
 * top bar is allowed to get ("Chrome caps at tinted").
 */
.search {
  --ds-input-group-block-size: var(--space__3xl);
  --ds-input-group-border-radius: var(--border-radius__full);
  --ds-input-group-background: var(--color__gray-100);
  --ds-input-group-border-block-width: 0;
  --ds-input-group-border-inline-start-width: 0;
  --ds-input-group-border-inline-end-width: 0;
  --ds-input-adornment-padding-inline-start: var(--space__lg);
  --ds-input-adornment-padding-inline-end: var(--space__md);
  /*
   * The trailing inset, which the field only needs because it is a pill: with no end adornment
   * the input keeps its own `padding-inline-end`, and the component's 8px default would ellipsise
   * the placeholder into the 20px curve. `--input-padding-inline-end` is the same property the
   * component zeroes when there *is* an end adornment, so this sets it rather than adding a
   * second rule the component does not know about.
   */
  --input-padding-inline-end: var(--space__lg);

  flex: 0 1 22.5rem;
  min-inline-size: 10rem;
  margin-inline: var(--space__sm);
}

/*
 * Hover, which has to be a fill here: the component says hover with `border-color`, and this
 * field has no border to say it with. One step down the same gray, so the field is the only
 * thing that changes and the bar around it does not (DESIGN.md rule 25 — nothing interactive is
 * rest-only). Focus is unchanged: the component's own ring already traces the `full` radius.
 *
 * Declared as the custom property rather than as `background-color`, because the component paints
 * that from the property on a `.group:not([data-disabled])` rule this one would otherwise tie with
 * on specificity, leaving the result to stylesheet order across two modules.
 */
.search [data-hovered] {
  --ds-input-group-background: var(--color__gray-200);
}

/*
 * The hint, one step darker than the design system sets it, because this field's ground is a tint
 * and that one's is white. `gray-500` is 4.9:1 on white and **4.4:1 on `gray-100`**, so carrying it
 * onto the well would put the placeholder under the AA floor — the exact failure
 * `design/10-foundations.md` "Text contrast" describes when it says `gray-500` is never used on a
 * tint. `gray-600` is 6.9:1 there.
 *
 * It also lands the hint on the same step as the magnifier, which `InputAdornment` already sets to
 * `gray-600` deliberately, so the glyph and the words it explains read as one thing rather than the
 * decoration outweighing the text.
 */
.search input::placeholder {
  color: var(--color__gray-600);
}

/*
 * The placeholder clears the moment the field takes focus, instead of surviving until the first
 * keystroke as a placeholder normally does (PLT-6648, from Janne's review of the shell on staging).
 * Clicking into a search field is already the statement of intent, and the hint that told you what
 * the box was for has nothing left to say once you are inside it.
 *
 * `color: transparent` rather than dropping the `placeholder` prop while focused: the attribute
 * stays, so the hint is still in the accessibility tree and still announced (verified on the
 * preview: focused, the placeholder computes to `rgba(0, 0, 0, 0)` and the attribute is still on
 * the input), and no React state ends up mirroring something the DOM already knows.
 *
 * `:focus`, not `:focus-visible`, because the state the ask is about is "the field has focus",
 * however focus arrived. The two do coincide on a field like this — an element that takes keyboard
 * input matches `:focus-visible` even when a mouse focused it, which the preview confirms — so
 * `:focus-visible` would behave the same today. No reason to hang the behaviour on that heuristic.
 *
 * Scoped to this slot deliberately. The behaviour belongs to a search, not to the design system's
 * `ComboBox`, which `portfolio-type-select` shares and a user who is not in the unified shell
 * renders: a value picker opens its list on focus and its placeholder is the "nothing selected"
 * hint, so that one has to keep it.
 */
.search input:focus::placeholder {
  color: transparent;
}

/*
 * One utility in the right cluster: 40px tall, `full` radius, ghost. The cluster is chrome,
 * so it caps at the tinted tier and nothing in it is the loudest thing on the page
 * (design/40-shell-and-pages.md "Top bar"). The hover ground is the one the shipped bar
 * paints, so the two bars stay one bar.
 */
.headerItem {
  display: flex;
  align-items: center;
  gap: var(--space__md);
  block-size: var(--space__3xl);
  padding-inline: var(--space__lg);
  border: none;
  border-radius: var(--border-radius__full);
  background: transparent;
  color: var(--color__gray-900);
  font: inherit;
  text-decoration: none;
  cursor: pointer;
}

.headerItem:hover {
  background-color: var(--bgColor-neutral-muted);
  color: var(--color__gray-900);
  text-decoration: none;
}

.headerItem:focus-visible {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
}

.headerItemIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color__gray-600);
}

.headerItemLabel {
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__medium);
  white-space: nowrap;
}

/* Menu rows use the rail's neutral ink, icon colour and pill-shaped hover target. */
.helpMenu > li,
.helpMenu [role='menuitem'] {
  border-radius: var(--border-radius__full);
  color: var(--color__gray-900);
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__medium);
}

.helpMenu [role='menuitem'] svg {
  color: var(--color__gray-600);
}

.helpMenu > li:hover {
  background-color: var(--bgColor-neutral-muted);
}

/*
 * The one colour event in the bar: the brand-yellow disc holding a dark sparkle, the chat
 * composer's send button at header scale. Yellow's single sanctioned role in product UI is
 * the AI entry point (design/60-ai-surface.md), so the pill itself stays ghost and the whole
 * accent budget goes into the mark.
 */
.sparkMark {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  inline-size: var(--space__xl);
  block-size: var(--space__xl);
  border-radius: var(--border-radius__full);
  background-color: var(--color__brand-yellow-loud);
  color: var(--color__gray-900);
}

.headerItem:hover .sparkMark {
  background-color: var(--color__brand-yellow-loud-hover);
}

/* The 40px hover box the account square sits in, `md` from the control before it. */
.accountWrap {
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--space__3xl);
  block-size: var(--space__3xl);
  margin-inline-start: var(--space__md);
  padding: 0;
  border: none;
  border-radius: var(--border-radius__full);
  background: transparent;
  cursor: pointer;
}

.accountWrap:hover {
  background-color: var(--bgColor-neutral-muted);
}

.accountWrap:focus-visible {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
}

/*
 * A 32px circle with white initials on `gray-900` — the unified shell's account avatar
 * as specified (design/50-components.md "Avatar").
 */
.account {
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--space__2xl);
  block-size: var(--space__2xl);
  border-radius: var(--border-radius__full);
  background: var(--color__gray-900);
  color: var(--color__white);
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__semibold);
}

/*
 * The account menu's legal footer: the two policy links and the data attribution, at the
 * incidental-meta step. A hairline above it rather than an `ActionList.Divider`, because this
 * block is not part of the list — the divider is a list item and this is what follows the list.
 *
 * `lg` inline, because that is what puts the links on the same edge as the rows above: Primer
 * insets its `ActionList` by 8px inside the overlay and each row's own padding adds 8 more, so
 * the list's visible left edge is its leading icons, 16px in. Measured, not guessed — at `md`
 * the links sat 4px proud of the icon column (design/10-foundations.md "Optical spacing":
 * measure between what a reader can see).
 */
.legalFooter {
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
  padding: var(--space__md) var(--space__lg) var(--space__sm);
  border-block-start: 1px solid var(--color__gray-300);
}

/*
 * The two blocks carry the type, not the links in them: one of the links is an inline run
 * inside `.legalNote`, and an inline run takes its leading from the paragraph it sits in
 * (design/10-foundations.md "Leading and measure"). `sm` pairs with `xs`, one step down.
 */
.legalLinks,
.legalNote {
  color: var(--color__gray-600);
  font-size: var(--font-size__sm);
  line-height: var(--line-height__xs);
}

.legalLinks {
  display: flex;
  gap: var(--space__md);
}

.legalNote {
  margin: 0;
}

.legalLink {
  color: var(--color__gray-600);
  text-decoration: none;
}

.legalLink:hover {
  color: var(--color__gray-900);
  text-decoration: underline;
}

.legalLink:focus-visible {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  outline-offset: var(--focus-ring__offset);
  border-radius: var(--border-radius__sm);
}

/*
 * Header degradation, in the order design/10-foundations.md "Layout and responsive" gives:
 * below `lg` the wordmark becomes the symbol and the utility labels drop to icon-only. The
 * logo and the account menu never disappear. A container query against the frame rather
 * than a media query, as the design system prefers for anything inside a shell.
 */
@container (max-inline-size: 63.25rem) {
  .logoFull {
    display: none;
  }

  .logoSymbol {
    display: block;
  }

  .headerItemLabel {
    display: none;
  }

  /* Icon-only, the pill is a 40px disc: a 20px glyph and a 24px spark mark both centre in it. */
  .headerItem {
    justify-content: center;
    inline-size: var(--space__3xl);
    padding-inline: 0;
  }
}

/*
 * Below `md` the rail is a drawer (`unified-shell-drawer.tsx`, PLT-6738), so the bar gains the
 * menu button that opens it and gives up the search field to make room.
 *
 * **The search moves rather than shrinks.** The field floors at 160px, which is a decision about
 * what a fund name needs rather than a number to shave; a 390px bar carrying a menu button, that
 * field and three utility discs is over its width before any of them gives. So the field goes to
 * the head of the drawer, where it has the panel's full width, and the bar keeps the controls a
 * reader reaches for one-handed. The trade is that finding a fund by name is two taps at this
 * width instead of one.
 *
 * The inline padding drops to `sm`: `xl` on both sides of a phone bar is 48px of the 390 spent on
 * nothing, and the menu button's own box already holds the glyph off the edge.
 */
@container (max-inline-size: 48rem) {
  .bar {
    padding-inline: var(--space__sm);
  }

  .menuButton {
    display: flex;
  }

  .searchSlot {
    display: none;
  }

  /*
   * The workspace pill gives way too. Its label is about 160px, and with the menu button, the
   * symbol and three utility discs a 390px bar is over its width before anything else gives.
   */
  .context {
    display: none;
  }
}
